(() => {
  if (window.ArchiveWatchlist) return;

  const STORAGE_KEY = 'archiveWatchlist';
  const WRITE_OWNER_KEY = 'twaArchiveWatchlistWriteOwner.v1';
  const CHANGE_EVENT = 'archive:watchlist-changed';
  const config = window.archiveWatchlistConfig || {};
  const customerId = normaliseCustomerId(config.customerId);

  let status = 'initializing';
  let items = [];
  let cloudProfile = null;
  let cloudPersistenceEnabled = false;
  let saveQueue = Promise.resolve();
  const subscribers = new Set();

  const cloneItems = () => items.map(item => ({ ...item }));

  function normaliseCustomerId(value) {
    if (value === null || value === undefined) return null;

    const normalised = String(value).trim();
    return /^\d+$/.test(normalised) ? normalised : null;
  }

  function readLocalWatchlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);

      if (raw === null) {
        return { present: false, valid: true, items: [] };
      }

      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? { present: true, valid: true, items: parsed }
        : { present: true, valid: false, items: [] };
    } catch (error) {
      console.warn('Unable to read Archive Watchlist from this browser.', error);
      return { present: true, valid: false, items: [] };
    }
  }

  function normaliseItem(item) {
    if (!item || item.id === undefined || item.id === null || String(item.id) === '') {
      return null;
    }

    const normalised = { id: String(item.id) };
    const fields = ['handle', 'title', 'price', 'url', 'image', 'region', 'type', 'communityReferenceEnabled', 'whiskybaseScore', 'addedAt'];

    fields.forEach(field => {
      if (item[field] !== undefined && item[field] !== null && item[field] !== '') {
        normalised[field] = item[field];
      }
    });

    return normalised;
  }

  function mergeItem(existing, incoming) {
    const existingTime = Date.parse(existing?.addedAt || '') || 0;
    const incomingTime = Date.parse(incoming?.addedAt || '') || 0;
    const newer = incomingTime >= existingTime ? incoming : existing;
    const older = newer === incoming ? existing : incoming;

    return { ...older, ...newer, id: String(newer.id || older.id) };
  }

  function reconcileWatchlists(cloudItems, localItems) {
    const reconciled = new Map();

    [...cloudItems, ...localItems].forEach(rawItem => {
      const item = normaliseItem(rawItem);
      if (!item) return;

      const id = String(item.id);
      const existing = reconciled.get(id);
      reconciled.set(id, existing ? mergeItem(existing, item) : item);
    });

    return [...reconciled.values()].sort((a, b) => {
      return (Date.parse(b.addedAt || '') || 0) - (Date.parse(a.addedAt || '') || 0);
    });
  }

  function sameProductIds(left, right) {
    const productIds = list => list
      .map(normaliseItem)
      .filter(Boolean)
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
      .map(item => String(item.id));

    const leftIds = productIds(Array.isArray(left) ? left : []);
    const rightIds = productIds(Array.isArray(right) ? right : []);
    return leftIds.length === rightIds.length
      && leftIds.every((value, index) => value === rightIds[index]);
  }

  function writeLocalWatchlist() {
    try {
      localStorage.setItem(WRITE_OWNER_KEY, JSON.stringify({
        customerId: config.isAuthenticated ? customerId : null,
        writtenAt: Date.now()
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      if (!config.isAuthenticated) {
        window.ArchiveOwnership?.captureAnonymousWatchlist(items);
      }
      return true;
    } catch (error) {
      console.error('Unable to save Archive Watchlist in this browser.', error);
      return false;
    }
  }

  function updateProductButtons() {
    const savedIds = new Set(items.map(item => String(item.id)));
    document.querySelectorAll('[data-archive-watchlist-toggle]').forEach(button => {
      const saved = savedIds.has(String(button.dataset.productId));
      button.dataset.watchlistSaved = saved ? 'true' : 'false';
      if (button.hasAttribute('data-archive-card-watchlist')) {
        // Resolve against the shared, ownership-approved state before revealing cards.
        button.hidden = status === 'initializing';
        button.setAttribute('aria-disabled', status !== 'ready' ? 'true' : 'false');
      } else {
        button.style.color = saved ? '#C6A96B' : 'inherit';
      }
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        saved ? 'Remove from Archive Watchlist' : 'Add to Archive Watchlist'
      );
      button.removeAttribute('aria-busy');
    });
  }

  function notify(source) {
    const detail = {
      items: cloneItems(),
      count: items.length,
      source,
      status
    };

    updateProductButtons();
    subscribers.forEach(callback => callback(detail));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail }));
  }

  function buildLoadUrl() {
    const url = new URL(config.loadEndpoint || '/apps/archive/profile/load', window.location.origin);
    if (customerId) url.searchParams.set('customerId', customerId);
    return `${url.pathname}${url.search}`;
  }

  async function loadCloudProfile() {
    if (!customerId) {
      throw new Error('Archive Watchlist customer configuration is unavailable.');
    }

    const response = await fetch(buildLoadUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Archive Profile load failed (${response.status}).`);
    }

    const result = await response.json();
    return result && typeof result.profile === 'object' && result.profile !== null
      ? result.profile
      : {};
  }

  async function persistCurrentWatchlist() {
    if (!config.isAuthenticated || !customerId || !cloudPersistenceEnabled || !cloudProfile) return false;

    const watchlist = cloneItems();

    const response = await fetch(config.saveEndpoint || '/apps/archive/profile/save', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        customerId,
        operation: 'Watchlist',
        profile: { watchlist }
      })
    });

    if (!response.ok) {
      throw new Error(`Archive Profile save failed (${response.status}).`);
    }

    const result = await response.json();
    cloudProfile = result && typeof result.profile === 'object'
      ? result.profile
      : cloudProfile;
    return true;
  }

  function showSaveError() {
    let notice = document.getElementById('archive-watchlist-save-error');
    if (!notice) {
      notice = document.createElement('p');
      notice.id = 'archive-watchlist-save-error';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'position:fixed;bottom:24px;right:24px;z-index:100;padding:16px;max-width:calc(100vw - 48px);background:#fff;color:#111;border:1px solid #C6A96B;';
      document.body.appendChild(notice);
    }
    notice.textContent = 'Unable to save your Watchlist. Your change was not saved. Please try again.';
    window.setTimeout(() => notice.remove(), 9000);
  }

  // Serialize full mutations, not just requests, so a failed save cannot roll
  // back a newer change or leak a failed mutation into the next save payload.
  function commit(change, source) {
    saveQueue = saveQueue.catch(() => false).then(async () => {
      if (status !== 'ready') {
        showSaveError();
        return false;
      }
      const previous = cloneItems();
      const next = change();
      if (!next) return false;
      items = reconcileWatchlists([], next);
      notify(source);
      try {
        if (config.isAuthenticated) {
          if (!await persistCurrentWatchlist()) throw new Error('Watchlist persistence is unavailable.');
          // Only broadcast confirmed account changes to other tabs.
          writeLocalWatchlist();
        } else if (!writeLocalWatchlist()) {
          throw new Error('Watchlist browser storage is unavailable.');
        }
        notify('saved');
        return true;
      } catch (error) {
        items = previous;
        notify('save-failed');
        showSaveError();
        console.error('Unable to sync Archive Watchlist.', error);
        return false;
      }
    });
    return saveQueue;
  }

  function isSaved(productId) {
    return items.some(item => String(item.id) === String(productId));
  }

  async function add(product) {
    await ready;
    return commit(() => {
      const item = normaliseItem({ ...product, addedAt: product.addedAt || new Date().toISOString() });
      return item && !isSaved(item.id) ? [...items, item] : null;
    }, 'add');
  }

  async function remove(productId) {
    await ready;
    return commit(() => isSaved(productId)
      ? items.filter(item => String(item.id) !== String(productId)) : null, 'remove');
  }

  async function clear() {
    await ready;
    return commit(() => [], 'clear');
  }

  async function toggle(product) {
    await ready;
    return commit(() => {
      if (isSaved(product.id)) return items.filter(item => String(item.id) !== String(product.id));
      const item = normaliseItem({ ...product, addedAt: product.addedAt || new Date().toISOString() });
      return item ? [...items, item] : null;
    }, 'toggle');
  }
  function subscribe(callback) {
    subscribers.add(callback);
    callback({ items: cloneItems(), count: items.length, source: 'subscribe', status });
    return () => subscribers.delete(callback);
  }

  let guestNoticeShown = false;

  function showGuestSaveNotice() {
    if (config.isAuthenticated || guestNoticeShown) return;

    const sessionKey = 'archiveWatchlistGuestNoticeShown';

    try {
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, 'true');
    } catch (error) {
      console.warn('Unable to remember the Archive Watchlist notice for this session.', error);
    }

    guestNoticeShown = true;

    if (!document.getElementById('archive-watchlist-guest-notice-style')) {
      const style = document.createElement('style');
      style.id = 'archive-watchlist-guest-notice-style';
      style.textContent = `
        .archive-watchlist-guest-toast {
          position: fixed;
          right: 24px;
          bottom: 24px;
          z-index: 50;
          width: min(360px, calc(100vw - 32px));
          padding: 20px 44px 20px 22px;
          border: 1px solid rgba(198, 169, 107, 0.38);
          background: #fff;
          color: #111;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          font-size: 14px;
          line-height: 1.55;
        }
        .archive-watchlist-guest-toast strong { display: block; margin-bottom: 5px; font-weight: 500; }
        .archive-watchlist-guest-toast p { margin: 0; color: #6f6f6f; }
        .archive-watchlist-guest-toast a { display: inline-block; margin-top: 10px; color: #9c7a36; text-underline-offset: 3px; }
        .archive-watchlist-guest-toast button {
          position: absolute;
          top: 10px;
          right: 12px;
          border: 0;
          background: transparent;
          color: #777;
          cursor: pointer;
          font-size: 20px;
          line-height: 1;
        }
        @media (max-width: 749px) {
          .archive-watchlist-guest-toast { right: 16px; bottom: 16px; }
        }
      `;
      document.head.appendChild(style);
    }

    const notice = document.createElement('aside');
    notice.className = 'archive-watchlist-guest-toast';
    notice.setAttribute('role', 'status');
    notice.innerHTML = `
      <button type="button" aria-label="Dismiss">&times;</button>
      <strong>Saved to your Watchlist</strong>
      <p>Sign in to keep your Watchlist with your Archive account and access it across devices.</p>
      <a href="/customer_authentication/login?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}">Sign in</a>
    `;

    notice.querySelector('button').addEventListener('click', () => notice.remove());
    document.body.appendChild(notice);
    window.setTimeout(() => notice.remove(), 9000);
  }

  async function initialise() {
    const ownership = window.ArchiveOwnership
      ? await window.ArchiveOwnership.ready
      : { decision: config.isAuthenticated ? 'fresh' : 'guest', watchlist: [] };
    const profileHandoff = ownership.decision === 'keep' && ownership.pendingAction?.action === 'save-profile'
      ? await window.ArchiveOwnership.profileHydrationReady
      : null;
    const watchlistHandoff = ownership.decision === 'keep' && ownership.pendingAction?.action === 'save-watchlist';
    const local = readLocalWatchlist();
    let validLocalItems = local.valid ? local.items : [];

    if (!config.isAuthenticated) {
      items = reconcileWatchlists([], validLocalItems);
      status = 'ready';
      notify('guest-initialized');
      return cloneItems();
    }

    validLocalItems = ownership.decision === 'keep'
      ? (Array.isArray(ownership.watchlist) ? ownership.watchlist : [])
      : [];

    if (profileHandoff && !profileHandoff.ok) {
      items = [];
      cloudPersistenceEnabled = false;
      status = 'degraded';
      notify('cloud-load-failed');
      return cloneItems();
    }

    try {
      const profile = await loadCloudProfile();
      const cloudItems = Array.isArray(profile.watchlist) ? profile.watchlist : [];
      const canonicalItems = reconcileWatchlists(cloudItems, validLocalItems);
      const profileForPersistence = watchlistHandoff && Object.keys(profile).length === 0
        ? (ownership.profile && typeof ownership.profile === 'object' ? ownership.profile : {})
        : profile;

      cloudProfile = profileForPersistence;
      cloudPersistenceEnabled = true;
      items = canonicalItems;
      writeLocalWatchlist();
      notify('cloud-reconciled');

      if (
        watchlistHandoff ||
        (!sameProductIds(cloudItems, canonicalItems) &&
          (!profileHandoff || profileHandoff.hasCloudProfile))
      ) {
        await persistCurrentWatchlist();
      }

      if (watchlistHandoff) {
        window.ArchiveOwnership?.consumePendingAction();
      }

      status = 'ready';
      notify('cloud-initialized');
      return cloneItems();
    } catch (error) {
      items = [];
      cloudPersistenceEnabled = false;
      status = 'degraded';
      notify('cloud-load-failed');
      console.error('Unable to hydrate Archive Watchlist.', error);
      return cloneItems();
    }
  }

  let resolveReady;
  const ready = new Promise(resolve => {
    resolveReady = resolve;
  });

  const api = {
    ready,
    get status() { return status; },
    getItems: cloneItems,
    isSaved,
    add,
    remove,
    clear,
    toggle,
    subscribe,
    flush: () => saveQueue
  };

  window.ArchiveWatchlist = api;

  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-archive-watchlist-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    button.setAttribute('aria-busy', 'true');

    const wasSaved = isSaved(button.dataset.productId);
    const changed = await toggle({
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      price: button.dataset.productPrice,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      region: button.dataset.productRegion,
      type: button.dataset.productType,
      communityReferenceEnabled: button.dataset.communityReferenceEnabled,
      whiskybaseScore: button.dataset.whiskybaseScore
    });

    if (!config.isAuthenticated && !wasSaved && changed) {
      showGuestSaveNotice();
    }
    updateProductButtons();
  }, { capture: true });

  // A restored document can retain both stale DOM and stale controller items.
  // Share one owner-checked refresh across lifecycle/storage signals, and order
  // it after initialization and pending saves so optimistic state cannot be lost.
  let reconciliation = null;
  function reconcileCurrentState(source) {
    if (reconciliation) return reconciliation;
    reconciliation = ready.then(() => {
      saveQueue = saveQueue.catch(() => false).then(() => {
        if (status !== 'ready') return;
        try {
          const writer = JSON.parse(localStorage.getItem(WRITE_OWNER_KEY) || '{}');
          const expectedWriter = config.isAuthenticated ? customerId : null;
          if ((writer.customerId ?? null) !== expectedWriter) return;
          const local = readLocalWatchlist();
          if (!local.valid || !local.present) return;
          const current = reconcileWatchlists([], local.items);
          if (JSON.stringify(current) === JSON.stringify(items)) {
            updateProductButtons();
            return;
          }
          items = current;
          notify(source);
        } catch (error) {
          console.warn('Unable to refresh Archive Watchlist.', error);
        }
      });
      return saveQueue;
    }).finally(() => { reconciliation = null; });
    return reconciliation;
  }

  window.addEventListener('storage', event => {
    if (event.key === STORAGE_KEY) reconcileCurrentState('storage');
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateProductButtons, { once: true });
  } else {
    updateProductButtons();
  }

  // Section rendering, filtering, pagination and recommendations insert new controls.
  // Observe structure and recycled product identities, never our own state attributes.
  const buttonSelector = '[data-archive-watchlist-toggle]';
  new MutationObserver(records => {
    if (records.some(record => record.type === 'attributes' || [...record.addedNodes].some(node =>
      node.nodeType === 1 && (node.matches(buttonSelector) || node.querySelector(buttonSelector))
    ))) updateProductButtons();
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-product-id'] });

  // pageshow also covers history paths that do not report persisted=true.
  // Visibility/focus cover resumed tabs; unchanged snapshots do not re-render
  // subscribers. None of these signals reinitializes ownership or fetches data.
  window.addEventListener('pageshow', () => reconcileCurrentState('pageshow'));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') reconcileCurrentState('pageshow');
  });
  window.addEventListener('focus', () => reconcileCurrentState('pageshow'));
  initialise().then(resolveReady);
})();
